import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import { AppError, ErrorType, ErrorDisplayOptions } from '../../types/error';
import { parseAxiosError, logError } from '../../utils/errorUtils';

interface ErrorState {
  error: AppError | null;
  isError: boolean;
}

interface ErrorActions {
  setError: (error: AppError | Error | string | null) => void;
  clearError: () => void;
  handleError: (error: any, context?: string) => AppError;
  handleAsyncError: <T>(
    asyncOperation: () => Promise<T>,
    context?: string,
    options?: ErrorDisplayOptions
  ) => Promise<T | null>;
}

export const useErrorHandler = (): ErrorState & ErrorActions => {
  const [error, setErrorState] = useState<AppError | null>(null);

  const setError = useCallback((error: AppError | Error | string | null) => {
    if (!error) {
      setErrorState(null);
      return;
    }

    let appError: AppError;

    if (typeof error === 'string') {
      appError = {
        type: ErrorType.UNKNOWN_ERROR,
        message: error,
      };
    } else if ('type' in error) {
      // Already an AppError
      appError = error;
    } else {
      // Generic Error
      appError = {
        type: ErrorType.UNKNOWN_ERROR,
        message: error.message || 'Bilinmeyen hata',
        originalError: error,
      };
    }

    setErrorState(appError);
    logError(appError);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  const handleError = useCallback(
    (error: any, context?: string): AppError => {
      let appError: AppError;

      if (error?.isAxiosError || error?.response) {
        // Axios error
        appError = parseAxiosError(error as AxiosError);
      } else if (typeof error === 'string') {
        appError = {
          type: ErrorType.UNKNOWN_ERROR,
          message: error,
        };
      } else if ('type' in error) {
        // Already an AppError
        appError = error;
      } else {
        // Generic error
        appError = {
          type: ErrorType.UNKNOWN_ERROR,
          message: error?.message || 'Bilinmeyen hata oluştu',
          originalError: error,
        };
      }

      if (context) {
        appError.context = { ...appError.context, operationContext: context };
      }

      setError(appError);
      logError(appError, context);

      return appError;
    },
    [setError]
  );

  const handleAsyncError = useCallback(
    async <T>(
      asyncOperation: () => Promise<T>,
      context?: string,
      options?: ErrorDisplayOptions
    ): Promise<T | null> => {
      try {
        clearError();
        const result = await asyncOperation();
        return result;
      } catch (error) {
        handleError(error, context);
        return null;
      }
    },
    [clearError, handleError]
  );

  return {
    error,
    isError: error !== null,
    setError,
    clearError,
    handleError,
    handleAsyncError,
  };
};

// Specialized hook for CRUD operations
export const useCrudErrorHandler = () => {
  const { error, isError, setError, clearError, handleError, handleAsyncError } = useErrorHandler();

  const handleCrudOperation = useCallback(
    async <T>(
      operation: () => Promise<T>,
      operationType: 'create' | 'update' | 'delete' | 'fetch',
      entityName?: string
    ): Promise<T | null> => {
      const context = `${operationType} ${entityName || 'entity'}`;
      return handleAsyncError(operation, context);
    },
    [handleAsyncError]
  );

  return {
    error,
    isError,
    setError,
    clearError,
    handleError,
    handleCrudOperation,
  };
};

// Hook for form validation errors
export const useValidationHandler = () => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const setFieldError = useCallback((field: string, message: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllFieldErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  const validateField = useCallback(
    (field: string, value: any, rules: ValidationRule[]): boolean => {
      for (const rule of rules) {
        const error = rule.validate(value);
        if (error) {
          setFieldError(field, error);
          return false;
        }
      }
      clearFieldError(field);
      return true;
    },
    [setFieldError, clearFieldError]
  );

  return {
    fieldErrors,
    setFieldError,
    clearFieldError,
    clearAllFieldErrors,
    validateField,
    hasFieldErrors: Object.keys(fieldErrors).length > 0,
  };
};

// Validation rule interface
interface ValidationRule {
  validate: (value: any) => string | null;
}

// Common validation rules
export const ValidationRules = {
  required: (fieldName: string): ValidationRule => ({
    validate: value => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return `${fieldName} zorunludur`;
      }
      return null;
    },
  }),

  email: (): ValidationRule => ({
    validate: value => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Geçerli bir email adresi girin';
      }
      return null;
    },
  }),

  phone: (): ValidationRule => ({
    validate: value => {
      if (value && !/^5\d{9}$/.test(value.replace(/\s/g, ''))) {
        return 'Geçerli bir telefon numarası girin (5XX XXX XX XX)';
      }
      return null;
    },
  }),

  minLength: (min: number): ValidationRule => ({
    validate: value => {
      if (value && value.length < min) {
        return `En az ${min} karakter olmalıdır`;
      }
      return null;
    },
  }),

  maxLength: (max: number): ValidationRule => ({
    validate: value => {
      if (value && value.length > max) {
        return `En fazla ${max} karakter olmalıdır`;
      }
      return null;
    },
  }),
};
